

import type {
  TrackingEventType,
  EventProperties,
  CartItem,
  ProductViewData,
  AddToCartData
} from '../types';

export interface EcommerceTrackerOptions {
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void;
}


export class EcommerceTracker {
  private onEvent: (type: TrackingEventType, properties?: EventProperties) => void;

  constructor(options: EcommerceTrackerOptions) {
    this.onEvent = options.onEvent;
  }

  
  trackProductView(data: ProductViewData): void {
    this.onEvent('sdk_product_view', {
      productId: data.productId,
      productName: data.productName,
      productPrice: data.productPrice,
      productCategory: data.productCategory,
      productBrand: data.productBrand,
      productVariant: data.productVariant
    });
  }

  
  trackAddToCart(data: AddToCartData): void {
    this.onEvent('sdk_add_to_cart', {
      productId: data.productId,
      productName: data.productName,
      productPrice: data.productPrice,
      quantity: data.quantity || 1,
      cartId: data.cartId,
      cartTotal: data.cartTotal
    });
  }

  
  trackRemoveFromCart(data: {
    productId: string;
    productName?: string;
    quantity?: number;
    cartId?: string;
  }): void {
    this.onEvent('sdk_remove_from_cart', {
      productId: data.productId,
      productName: data.productName,
      quantity: data.quantity || 1,
      cartId: data.cartId
    });
  }

  
  trackCheckoutStep(data: {
    step: number;
    stepName?: string;
    cartId?: string;
    cartTotal?: number;
    items?: CartItem[];
  }): void {
    this.onEvent('sdk_checkout_step', {
      checkoutStep: data.step,
      checkoutStepName: data.stepName,
      cartId: data.cartId,
      cartTotal: data.cartTotal,
      items: data.items
    });
  }

  
  trackSearch(data: { query: string; resultsCount?: number }): void {
    this.onEvent('sdk_search', {
      searchQuery: data.query,
      searchResultsCount: data.resultsCount
    });
  }
}




export function setupVtexIntegration(
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  if (typeof window === 'undefined') return;

  const win = window as unknown as { dataLayer?: unknown[] };

  
  if (win.dataLayer && typeof win.dataLayer.push === 'function') {
    interceptDataLayer(win.dataLayer, onEvent);
    return;
  }

  
  
  
  if (!win.dataLayer) {
    win.dataLayer = [];
  }
  interceptDataLayer(win.dataLayer, onEvent);

  
  let attempts = 0;
  const maxAttempts = 20; 
  const pollInterval = setInterval(() => {
    attempts++;
    if (attempts >= maxAttempts) {
      clearInterval(pollInterval);
      return;
    }
    
    if (win.dataLayer && !(win.dataLayer as unknown as { _twiggy?: boolean })._twiggy) {
      interceptDataLayer(win.dataLayer, onEvent);
      clearInterval(pollInterval);
    }
  }, 500);
}

function interceptDataLayer(
  dataLayer: unknown[],
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  
  if ((dataLayer as unknown as { _twiggy?: boolean })._twiggy) return;
  (dataLayer as unknown as { _twiggy: boolean })._twiggy = true;

  const originalPush = dataLayer.push.bind(dataLayer);

  dataLayer.push = function (...args: unknown[]) {
    
    args.forEach(item => {
      try {
        processVtexEvent(item, onEvent);
      } catch {
        
      }
    });

    return originalPush(...args);
  };

  
  for (const item of dataLayer) {
    try {
      processVtexEvent(item, onEvent);
    } catch {
      
    }
  }
}

/**
 * Intercepta apenas eventos de lead via dataLayer, sem depender de VTEX/GA4.
 * Isso permite capturar `checkout_lead` em qualquer plataforma (wake/linx/tray/etc)
 * quando o cliente fizer `dataLayer.push({ event: 'checkout_lead', ... })`.
 */
export function setupDataLayerLeadCapture(
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { dataLayer?: unknown[] };
  const dl = w.dataLayer;
  if (!dl || typeof (dl as unknown[]).push !== 'function') return;

  const marker = dl as unknown as { _twiggyLead?: boolean; push: (...args: unknown[]) => number };
  if (marker._twiggyLead) return;
  marker._twiggyLead = true;

  const originalPush = dl.push.bind(dl);
  dl.push = function (...args: unknown[]) {
    args.forEach(item => {
      try {
        if (item && typeof item === 'object' && (item as any).event === 'checkout_lead') {
          emitCheckoutLeadFromDataLayer(item as Record<string, unknown>, onEvent);
        }
      } catch {
        // ignore
      }
    });
    return originalPush(...args);
  };

  // Processa eventos já existentes no array
  for (const item of dl) {
    try {
      if (item && typeof item === 'object' && (item as any).event === 'checkout_lead') {
        emitCheckoutLeadFromDataLayer(item as Record<string, unknown>, onEvent);
      }
    } catch {
      // ignore
    }
  }
}

/**
 * Wishlist / lista de desejos via GTM: `dataLayer.push({ event: 'twiggy_wishlist', ... })`.
 * Ative com `Twiggy.init({ ..., wishlistDataLayer: true })` (ex.: Shopify / Amaro).
 */
const TWIGGY_ANON_WL_PREFIX = 'twiggy-anon-wl-';

export function setupDataLayerWishlistCapture(
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void,
  getVisitorId?: () => string | null
): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { dataLayer?: unknown[] };
  const dl = w.dataLayer;
  if (!dl || typeof (dl as unknown[]).push !== 'function') return;

  const marker = dl as unknown as { _twiggyWishlist?: boolean; push: (...args: unknown[]) => number };
  if (marker._twiggyWishlist) return;
  marker._twiggyWishlist = true;

  const originalPush = dl.push.bind(dl);
  dl.push = function (...args: unknown[]) {
    args.forEach(item => {
      try {
        if (!item || typeof item !== 'object') return;
        const ev = (item as Record<string, unknown>).event;
        if (ev === 'twiggy_wishlist' || ev === 'wishlist_signal') {
          emitWishlistSignalFromDataLayer(
            item as Record<string, unknown>,
            onEvent,
            getVisitorId
          );
        }
      } catch {
        // ignore
      }
    });
    return originalPush(...args);
  };

  for (const item of dl) {
    try {
      if (!item || typeof item !== 'object') continue;
      const ev = (item as Record<string, unknown>).event;
      if (ev === 'twiggy_wishlist' || ev === 'wishlist_signal') {
        emitWishlistSignalFromDataLayer(
          item as Record<string, unknown>,
          onEvent,
          getVisitorId
        );
      }
    } catch {
      // ignore
    }
  }
}

function mapWishlistLineItemsToCartItems(items: unknown[]): CartItem[] {
  return items.map((raw, i) => {
    if (!raw || typeof raw !== 'object') {
      return { productId: `unknown-${i}`, quantity: 1 };
    }
    const it = raw as Record<string, unknown>;
    const productId = String(
      it.productId ?? it.id ?? it.variant_id ?? it.variantId ?? `item-${i}`
    );
    const quantity = Number(it.quantity) || 1;
    const productName =
      it.name != null || it.title != null || it.productName != null
        ? String(it.name ?? it.title ?? it.productName)
        : undefined;
    const priceRaw = it.price ?? it.productPrice ?? it.sellingPrice;
    const price = Number(priceRaw);
    const productPrice = Number.isFinite(price) && price > 0 ? price : undefined;
    return { productId, productName, productPrice, quantity };
  });
}

function sumWishlistItemsCents(items: unknown[]): number {
  let sum = 0;
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const it = raw as Record<string, unknown>;
    const q = Number(it.quantity) || 1;
    const price = Number(it.price ?? it.productPrice ?? it.sellingPrice ?? 0);
    if (Number.isFinite(price)) sum += price * q;
  }
  return Math.round(sum);
}

function emitWishlistSignalFromDataLayer(
  event: Record<string, unknown>,
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void,
  getVisitorId?: () => string | null
): void {
  const items = event.items;
  if (!Array.isArray(items) || items.length === 0) return;

  const phoneRaw =
    (typeof event.customerPhone === 'string' && event.customerPhone) ||
    (typeof event.phone === 'string' && event.phone) ||
    '';
  const nameRaw =
    (typeof event.customerName === 'string' && event.customerName) ||
    (typeof event.name === 'string' && event.name) ||
    '';

  const phone = phoneRaw.trim();
  let wishlistId =
    (typeof event.wishlistId === 'string' && event.wishlistId) ||
    (typeof event.cartId === 'string' && event.cartId) ||
    (typeof event.orderFormId === 'string' && event.orderFormId) ||
    '';

  const emailVal = event.email;
  const customerEmail =
    emailVal === null || emailVal === undefined || emailVal === ''
      ? undefined
      : String(emailVal).trim();

  const shopifyCustomerIdRaw = event.shopifyCustomerId;
  const shopifyCustomerId =
    shopifyCustomerIdRaw !== null && shopifyCustomerIdRaw !== undefined
      ? String(shopifyCustomerIdRaw).trim()
      : undefined;

  if (!phone && !customerEmail && !shopifyCustomerId) {
    const vid = getVisitorId?.();
    if (!vid || String(vid).trim() === '') return;
    if (!wishlistId) {
      wishlistId = `${TWIGGY_ANON_WL_PREFIX}${String(vid).trim()}`;
    }
  }

  if (!wishlistId) return;

  const nameTrimmed = nameRaw.trim();
  const customerName = displayNameFromProfileParts(nameTrimmed, customerEmail, phone);

  const totalFromEvent =
    typeof event.total === 'number'
      ? event.total
      : typeof event.value === 'number'
        ? event.value
        : sumWishlistItemsCents(items);

  const wishlistUrl =
    (typeof event.wishlistUrl === 'string' && event.wishlistUrl) ||
    (typeof event.recoveryUrl === 'string' && event.recoveryUrl) ||
    undefined;

  const action =
    typeof event.action === 'string' && event.action ? event.action : 'update';

  onEvent('sdk_wishlist_signal', {
    cartId: wishlistId,
    orderFormId: wishlistId,
    customerName,
    ...(phone ? { customerPhone: phone } : {}),
    ...(customerEmail ? { customerEmail } : {}),
    ...(shopifyCustomerId ? { shopifyCustomerId } : {}),
    items: mapWishlistLineItemsToCartItems(items),
    ...(totalFromEvent > 0 ? { total: totalFromEvent } : {}),
    ...(wishlistUrl ? { wishlistUrl } : {}),
    wishlistAction: action
  });
}

function processVtexEvent(
  item: unknown,
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  if (!item || typeof item !== 'object') return;

  const event = item as Record<string, unknown>;

  
  
  
  if (event[0] === 'event' && typeof event[1] === 'string' && event['length']) {
    processGtagEvent(
      event[1] as string,
      (event[2] as Record<string, unknown>) || undefined,
      onEvent
    );
    return;
  }

  
  if (event.event === 'productView' && event.ecommerce) {
    const ecommerce = event.ecommerce as { detail?: { products?: Array<Record<string, unknown>> } };
    const product = ecommerce.detail?.products?.[0];
    if (product) {
      onEvent('sdk_product_view', {
        productId: String(product.id || product.productId),
        productName: String(product.name),
        productPrice: Number(product.price),
        productCategory: String(product.category || ''),
        productBrand: String(product.brand || '')
      });
    }
  }

  
  if (event.event === 'addToCart' && event.ecommerce) {
    const ecommerce = event.ecommerce as { add?: { products?: Array<Record<string, unknown>> } };
    const product = ecommerce.add?.products?.[0];
    if (product) {
      onEvent('sdk_add_to_cart', {
        productId: String(product.id || product.productId),
        productName: String(product.name),
        productPrice: Number(product.price),
        quantity: Number(product.quantity) || 1
      });
    }
  }

  
  if (event.event === 'removeFromCart' && event.ecommerce) {
    const ecommerce = event.ecommerce as { remove?: { products?: Array<Record<string, unknown>> } };
    const product = ecommerce.remove?.products?.[0];
    if (product) {
      onEvent('sdk_remove_from_cart', {
        productId: String(product.id || product.productId),
        productName: String(product.name),
        quantity: Number(product.quantity) || 1
      });
    }
  }

  
  if (event.event === 'checkout' && event.ecommerce) {
    const ecommerce = event.ecommerce as {
      checkout?: { actionField?: { step?: number }; products?: Array<Record<string, unknown>> };
    };
    const checkout = ecommerce.checkout;
    if (checkout) {
      onEvent('sdk_checkout_step', {
        checkoutStep: checkout.actionField?.step || 1,
        items: checkout.products?.map(p => ({
          productId: String(p.id || p.productId),
          productName: String(p.name),
          productPrice: Number(p.price),
          quantity: Number(p.quantity) || 1
        }))
      });
    }
  }

  if (event.event === 'cart_abandoned') {
    emitCartAbandonedFromDataLayer(event, onEvent);
  }

  // checkout_lead: tratado só em setupDataLayerLeadCapture (camada externa do push),
  // para não disparar sdk_checkout_lead duas vezes quando VTEX + lead capture estão ativos.
}

/**
 * VTEX orderForm.items → CartItem[] for the event queue.
 */
function mapVtexOrderFormItemsToCartItems(items: unknown[]): CartItem[] {
  return items.map((raw, i) => {
    if (!raw || typeof raw !== 'object') {
      return { productId: `unknown-${i}`, quantity: 1 };
    }
    const it = raw as Record<string, unknown>;
    const productId = String(it.productId ?? it.id ?? `item-${i}`);
    const quantity = Number(it.quantity) || 1;
    const productName = it.name != null ? String(it.name) : undefined;
    const price = Number(it.sellingPrice ?? it.price ?? it.salePrice ?? 0);
    const productPrice = Number.isFinite(price) && price > 0 ? price : undefined;
    return { productId, productName, productPrice, quantity };
  });
}

function sumVtexItemsTotal(items: unknown[]): number {
  let sum = 0;
  for (const raw of items) {
    if (!raw || typeof raw !== 'object') continue;
    const it = raw as Record<string, unknown>;
    const q = Number(it.quantity) || 1;
    const price = Number(it.sellingPrice ?? it.price ?? it.salePrice ?? 0);
    if (Number.isFinite(price)) sum += price * q;
  }
  return sum;
}

/**
 * Nome amigável para CRM quando o checkout ainda não tem firstName/lastName.
 */
function displayNameFromProfileParts(
  nameTrimmed: string,
  emailTrimmed: string | undefined,
  phoneTrimmed: string
): string {
  if (nameTrimmed) return nameTrimmed;
  if (emailTrimmed) {
    const local = emailTrimmed.split('@')[0]?.trim();
    if (local) return local;
  }
  if (phoneTrimmed) return 'Cliente';
  return 'Cliente';
}

/**
 * GTM / dataLayer `cart_abandoned`: itens + orderFormId + (e-mail OU telefone). Nome opcional.
 * Quem exige telefone/nome completo para ir ao kanban é o backend (promoção CRM vs só contatos).
 */
function emitCartAbandonedFromDataLayer(
  event: Record<string, unknown>,
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  const items = event.items;
  if (!Array.isArray(items) || items.length === 0) return;

  const phoneRaw =
    (typeof event.customerPhone === 'string' && event.customerPhone) ||
    (typeof event.phone === 'string' && event.phone) ||
    '';
  const nameRaw =
    (typeof event.customerName === 'string' && event.customerName) ||
    (typeof event.name === 'string' && event.name) ||
    '';

  const phone = phoneRaw.trim();
  const orderFormId =
    (typeof event.orderFormId === 'string' && event.orderFormId) ||
    (typeof event.cartId === 'string' && event.cartId) ||
    '';
  if (!orderFormId) return;

  const emailVal = event.email;
  const customerEmail =
    emailVal === null || emailVal === undefined || emailVal === ''
      ? undefined
      : String(emailVal).trim();

  if (!phone && !customerEmail) return;

  const nameTrimmed = nameRaw.trim();
  const customerName = displayNameFromProfileParts(nameTrimmed, customerEmail, phone);

  const totalFromEvent =
    typeof event.total === 'number'
      ? event.total
      : typeof event.value === 'number'
        ? event.value
        : sumVtexItemsTotal(items);

  onEvent('sdk_cart_abandoned', {
    orderFormId,
    cartId: orderFormId,
    customerName,
    ...(phone ? { customerPhone: phone } : {}),
    ...(customerEmail ? { customerEmail } : {}),
    items: mapVtexOrderFormItemsToCartItems(items),
    ...(totalFromEvent > 0 ? { total: totalFromEvent } : {})
  });
}

/**
 * Lead de checkout: e-mail ou telefone; nome opcional (fallback alinhado ao abandono).
 */
function emitCheckoutLeadFromDataLayer(
  event: Record<string, unknown>,
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  const phoneRaw =
    (typeof event.customerPhone === 'string' && event.customerPhone) ||
    (typeof event.phone === 'string' && event.phone) ||
    '';
  const nameRaw =
    (typeof event.customerName === 'string' && event.customerName) ||
    (typeof event.name === 'string' && event.name) ||
    '';

  const phone = phoneRaw.trim();
  const nameTrimmed = nameRaw.trim();

  const emailVal = event.email;
  const customerEmail =
    emailVal === null || emailVal === undefined || emailVal === ''
      ? undefined
      : String(emailVal).trim();

  if (!customerEmail && !phone) return;

  const orderFormId =
    (typeof event.orderFormId === 'string' && event.orderFormId) ||
    (typeof event.cartId === 'string' && event.cartId) ||
    undefined;

  const customerName = displayNameFromProfileParts(nameTrimmed, customerEmail, phone);

  onEvent('sdk_checkout_lead', {
    ...(orderFormId ? { orderFormId, cartId: orderFormId } : {}),
    customerName,
    ...(phone ? { customerPhone: phone } : {}),
    ...(customerEmail ? { customerEmail } : {}),
    hasItems: typeof event.hasItems === 'boolean' ? event.hasItems : undefined
  });
}

let vtexCartAbandonmentListenersAttached = false;
/** Evita log repetido enquanto `ensure` / intervalo tentam de novo. */
let vtexAbandonCheckoutMissingLogged = false;

const VTEX_ABANDON_LAZY_MS = 2000;

let vtexAbandonLazyArmRegistered = false;
let vtexAbandonLazyIntervalId: ReturnType<typeof setInterval> | null = null;

function clearVtexAbandonLazyInterval(): void {
  if (vtexAbandonLazyIntervalId != null) {
    clearInterval(vtexAbandonLazyIntervalId);
    vtexAbandonLazyIntervalId = null;
  }
}

function trySetupVtexCartAbandonmentOnce(): void {
  if (vtexCartAbandonmentListenersAttached) {
    clearVtexAbandonLazyInterval();
    return;
  }
  setupVtexCartAbandonmentTracking();
  if (vtexCartAbandonmentListenersAttached) clearVtexAbandonLazyInterval();
}

/**
 * Vitrine → checkout SPA: `vtexjs.checkout` pode surgir depois. Sem monkey-patch de `history`,
 * re-tentamos em popstate, focus, visibility e intervalo leve até anexar (ou fim da sessão da aba).
 */
function registerVtexAbandonmentLazyArm(): void {
  if (typeof window === 'undefined' || vtexAbandonLazyArmRegistered) return;
  vtexAbandonLazyArmRegistered = true;

  const lazyTry = (): void => {
    trySetupVtexCartAbandonmentOnce();
  };

  window.addEventListener('popstate', lazyTry);
  window.addEventListener('focus', lazyTry);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') lazyTry();
  });

  // Sem limite de tempo: jornadas longas na vitrine + SPA só com pushState (sem popstate) deixavam de
  // re-tentar após ~6 min. O intervalo para assim que os listeners forem anexados ou ao fechar a aba.
  vtexAbandonLazyIntervalId = setInterval(() => {
    lazyTry();
    if (vtexCartAbandonmentListenersAttached) clearVtexAbandonLazyInterval();
  }, VTEX_ABANDON_LAZY_MS);
}

/**
 * Tenta anexar listeners de abandono agora; se `vtexjs.checkout` ainda não existir, agenda re-tentativas leves
 * (SPA vitrine → checkout sem reload).
 */
export function ensureVtexCartAbandonmentTracking(): void {
  if (typeof window === 'undefined') return;
  trySetupVtexCartAbandonmentOnce();
  if (!vtexCartAbandonmentListenersAttached) {
    registerVtexAbandonmentLazyArm();
  }
}

/**
 * Dispara `dataLayer.push({ event: 'cart_abandoned', ... })` em `pagehide` / `beforeunload`
 * quando há itens + orderFormId + (e-mail ou telefone). Nome opcional (fallback no perfil).
 * Regras de promoção ao CRM vs contato ficam no servidor (`track-abandonment` / sync-crm).
 *
 * Se `vtexjs.checkout` ainda não existir, não anexa nada — prefira {@link ensureVtexCartAbandonmentTracking} no `Twiggy.init`.
 */
export function setupVtexCartAbandonmentTracking(): void {
  if (typeof window === 'undefined') return;
  if (vtexCartAbandonmentListenersAttached) return;

  const w = window as unknown as {
    vtexjs?: { checkout?: { getOrderForm?: () => Promise<unknown> } };
    dataLayer?: unknown[];
  };

  if (!w.vtexjs?.checkout) {
    if (!vtexAbandonCheckoutMissingLogged) {
      vtexAbandonCheckoutMissingLogged = true;
      console.log(
        '[Twiggy] VTEX checkout ainda não disponível; abandono será anexado quando `vtexjs.checkout` existir (ensure / GTM).'
      );
    }
    return;
  }
  vtexAbandonCheckoutMissingLogged = false;

  /** Só bloqueia após `cart_abandoned` ir ao dataLayer (evita perder abandono se o 1º getOrderForm vier vazio e o 2º evento da página tiver itens). */
  let abandonEmitted = false;
  /** Evita duas chamadas concorrentes (ex.: `pagehide` + `beforeunload`). */
  let captureInFlight = false;

  const runCartCapture = (): void => {
    if (abandonEmitted || captureInFlight) return;
    if (!w.vtexjs?.checkout?.getOrderForm) return;

    captureInFlight = true;
    // VTEX costuma expor jQuery Deferred, não Promise nativa — `Promise.resolve` normaliza.
    void Promise.resolve(w.vtexjs.checkout.getOrderForm())
      .then((orderForm: unknown) => {
        if (abandonEmitted) return;

        if (!orderForm || typeof orderForm !== 'object') return;
        const of = orderForm as Record<string, unknown>;
        const rawItems = of.items as Array<Record<string, unknown>> | undefined;

        const profile = (of.clientProfileData || {}) as Record<string, unknown>;
        const firstName = profile.firstName as string | undefined;
        const lastName = profile.lastName as string | undefined;
        const profileName = [firstName, lastName].filter(Boolean).join(' ').trim();
        const phone =
          (profile.phone as string | undefined) ||
          (profile.homePhone as string | undefined) ||
          (profile.cellPhone as string | undefined);
        const email = profile.email as string | undefined;

        const phoneStr = phone != null && phone !== '' ? String(phone).trim() : '';
        const emailStr = email != null && email !== '' ? String(email).trim() : '';

        const orderFormId = of.orderFormId as string | undefined;
        if (!orderFormId) return;

        if (!w.dataLayer) {
          w.dataLayer = [];
        }

        const leadDisplayName = displayNameFromProfileParts(profileName, emailStr, phoneStr);

        // 1) Lead — e-mail ou telefone; nome opcional (fallback para CRM)
        if (emailStr || phoneStr) {
          w.dataLayer.push({
            event: 'checkout_lead',
            orderFormId,
            customerName: leadDisplayName,
            customerPhone: phoneStr || null,
            email: emailStr || null,
            hasItems: Array.isArray(rawItems) && rawItems.length > 0
          });
        }

        // 2) Abandono: itens + pelo menos e-mail ou telefone (checkout preenche em etapas)
        if (!rawItems || rawItems.length === 0) {
          console.log('[Twiggy] Carrinho vazio, não enviar');
          return;
        }

        if (!phoneStr && !emailStr) {
          console.log(
            '[Twiggy] Sem e-mail nem telefone no orderForm — abandono não enviado (identifique o cliente primeiro)'
          );
          return;
        }

        const abandonDisplayName = displayNameFromProfileParts(profileName, emailStr, phoneStr);

        const items = rawItems.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.sellingPrice
        }));

        w.dataLayer.push({
          event: 'cart_abandoned',
          orderFormId,
          customerName: abandonDisplayName,
          customerPhone: phoneStr || null,
          email: emailStr || null,
          items
        });
        abandonEmitted = true;
      })
      .catch((err: unknown) => {
        console.error('[Twiggy] Erro ao buscar orderForm:', err);
      })
      .finally(() => {
        captureInFlight = false;
      });
  };

  const trackCartAbandonment = (): void => {
    if (abandonEmitted) return;
    // Sem debounce: em `pagehide`/`beforeunload` qualquer atraso costuma matar o callback async.
    runCartCapture();
  };

  window.addEventListener('pageshow', () => {
    abandonEmitted = false;
  });
  // Capture: roda antes do `pagehide` em bubble do EventQueue (`flushSync`), dando mais tempo ao getOrderForm.
  window.addEventListener('pagehide', trackCartAbandonment, { capture: true });
  window.addEventListener('beforeunload', trackCartAbandonment, { capture: true });

  vtexCartAbandonmentListenersAttached = true;
  console.log('[Twiggy] Rastreamento de abandono de carrinho VTEX configurado');
}

/**
 * Alias histórico: mesmo comportamento que {@link ensureVtexCartAbandonmentTracking}.
 */
export function setupVtexCartAbandonmentTrackingWithPoll(): void {
  ensureVtexCartAbandonmentTracking();
}


export function setupGtagIntegration(
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  if (typeof window === 'undefined') return;

  const w = window as unknown as {
    gtag?: ((...args: unknown[]) => void) & { _twiggy?: boolean };
  };

  function tryIntercept(): boolean {
    if (!w.gtag || w.gtag._twiggy) return !!w.gtag;
    const originalGtag = w.gtag;

    const intercepted = function (...args: unknown[]) {
      if (args[0] === 'event' && typeof args[1] === 'string') {
        try {
          processGtagEvent(args[1], args[2] as Record<string, unknown>, onEvent);
        } catch {
          
        }
      }
      return originalGtag(...args);
    };
    intercepted._twiggy = true;
    w.gtag = intercepted;
    return true;
  }

  if (tryIntercept()) return;

  
  let attempts = 0;
  const pollInterval = setInterval(() => {
    attempts++;
    if (attempts >= 20 || tryIntercept()) {
      clearInterval(pollInterval);
    }
  }, 500);
}

function processGtagEvent(
  eventName: string,
  params: Record<string, unknown> | undefined,
  onEvent: (type: TrackingEventType, properties?: EventProperties) => void
): void {
  if (!params) return;

  switch (eventName) {
    case 'view_item':
      if (params.items && Array.isArray(params.items)) {
        const item = params.items[0] as Record<string, unknown>;
        onEvent('sdk_product_view', {
          productId: String(item.item_id),
          productName: String(item.item_name),
          productPrice: Number(item.price),
          productCategory: String(item.item_category || ''),
          productBrand: String(item.item_brand || '')
        });
      }
      break;

    case 'add_to_cart':
      if (params.items && Array.isArray(params.items)) {
        const item = params.items[0] as Record<string, unknown>;
        onEvent('sdk_add_to_cart', {
          productId: String(item.item_id),
          productName: String(item.item_name),
          productPrice: Number(item.price),
          quantity: Number(item.quantity) || 1,
          cartTotal: Number(params.value)
        });
      }
      break;

    case 'remove_from_cart':
      if (params.items && Array.isArray(params.items)) {
        const item = params.items[0] as Record<string, unknown>;
        onEvent('sdk_remove_from_cart', {
          productId: String(item.item_id),
          productName: String(item.item_name),
          quantity: Number(item.quantity) || 1
        });
      }
      break;

    case 'begin_checkout':
      onEvent('sdk_checkout_step', {
        checkoutStep: 1,
        checkoutStepName: 'begin_checkout',
        cartTotal: Number(params.value),
        items: (params.items as Array<Record<string, unknown>>)?.map(item => ({
          productId: String(item.item_id),
          productName: String(item.item_name),
          productPrice: Number(item.price),
          quantity: Number(item.quantity) || 1
        }))
      });
      break;

    case 'search':
      onEvent('sdk_search', {
        searchQuery: String(params.search_term)
      });
      break;
  }
}
